import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Code, History, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import type { VersaoSite } from '@nyroh/contracts';
import { db } from '../services/db';

/**
 * ============================================================================
 * HISTÓRICO DE VERSÕES DE UM SITE
 * ============================================================================
 *
 * Cada publicacao grava um artefato IMUTAVEL no R2
 * (`sites/{siteId}/{versaoId}.html`) e move um ponteiro no banco
 * (`site.versaoAtualId`). E esse ponteiro, e so ele, que decide o que a borda
 * serve.
 *
 * O painel nao mostrava nada disso. Quem editava um site duas vezes via dois
 * arquivos no bucket e nao tinha como saber qual estava no ar, nem como voltar
 * ao anterior — a unica saida era republicar por cima, o que cria uma TERCEIRA
 * versao e nao resolve.
 *
 * "Colocar no ar" aqui NAO regera nada: chama `sites.reverter`, que troca o
 * ponteiro e invalida o KV da borda. O artefato antigo continua exatamente como
 * foi publicado — e por isso a operacao e instantanea e nao depende de o
 * gerador ainda produzir o mesmo HTML.
 */

interface Props {
  siteId: string;
  slug: string;
  /** Chamado depois de trocar a versao, para a tela de cima recarregar o cliente. */
  aoTrocarVersao?: () => void;
  /** Abre o código de uma versão no visualizador da tela de cima. */
  aoVerCodigo?: (html: string, versao: VersaoSite) => void;
}

function formatarData(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatarBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const VersoesDoSite: React.FC<Props> = ({ siteId, slug, aoTrocarVersao, aoVerCodigo }) => {
  const [versoes, setVersoes] = useState<VersaoSite[]>([]);
  const [versaoAtualId, setVersaoAtualId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [trocando, setTrocando] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const r = await db.listSiteVersions(siteId);
      setVersoes(r.versoes);
      setVersaoAtualId(r.versaoAtualId);
    } catch (e) {
      // Nunca `catch {}` vazio: sem a mensagem, "a lista nao carrega" e tudo o
      // que se sabe, e a causa (401, CORS, site apagado) fica invisivel.
      console.error('[versoes] falha ao listar', e);
      setErro(e instanceof Error ? e.message : 'não consegui carregar o histórico.');
    } finally {
      setCarregando(false);
    }
  }, [siteId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const ativar = async (versao: VersaoSite) => {
    const jaEra = versao.id === versaoAtualId;
    setTrocando(versao.id);
    try {
      await db.activateSiteVersion(siteId, versao.id, slug);
      setVersaoAtualId(versao.id);
      toast.success(
        jaEra
          ? 'Reenviado à borda. Recarregue o site público — se ainda vier o antigo, faltam as quatro CF_* na API.'
          : 'Versão no ar. A borda foi invalidada — recarregue o site público.',
      );
      aoTrocarVersao?.();
    } catch (e) {
      console.error('[versoes] falha ao ativar', e);
      toast.error(e instanceof Error ? e.message : 'não consegui trocar a versão.');
    } finally {
      setTrocando(null);
    }
  };

  const verCodigo = async (versao: VersaoSite) => {
    if (!aoVerCodigo) return;
    setAbrindo(versao.id);
    try {
      const html = await db.getVersionHtml(siteId, versao.id);
      aoVerCodigo(html, versao);
    } catch (e) {
      console.error('[versoes] falha ao ler o artefato', e);
      toast.error(e instanceof Error ? e.message : 'não consegui ler o HTML dessa versão.');
    } finally {
      setAbrindo(null);
    }
  };

  return (
    <div className="pt-6 border-t border-gray-100">
      <div className="flex items-center justify-between gap-4 mb-3">
        <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
          <History size={12} /> Versões publicadas
        </label>
        <button
          onClick={() => void carregar()}
          disabled={carregando}
          className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded-md transition-colors disabled:opacity-40"
          title="Recarregar"
        >
          <RefreshCw size={14} className={carregando ? 'animate-spin' : ''} />
        </button>
      </div>

      {carregando && (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
          <Loader2 size={14} className="animate-spin" /> carregando o histórico…
        </div>
      )}

      {!carregando && erro && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-xs">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{erro}</span>
        </div>
      )}

      {!carregando && !erro && versoes.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs">
          <b>Nenhuma versão publicada.</b> O site existe no banco, mas nada foi para o R2 —
          é por isso que o endereço público responde em branco. Abra o <b>Editor Visual</b> e
          salve: é o salvar que renderiza o artefato e o publica.
        </div>
      )}

      {!carregando && !erro && versoes.length > 0 && (
        <ul className="space-y-2">
          {versoes.map((v) => {
            const noAr = v.id === versaoAtualId;
            return (
              <li
                key={v.id}
                className={`flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border p-3 ${
                  noAr ? 'border-green-300 bg-green-50/60' : 'border-gray-200 bg-gray-50/50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{formatarData(v.criadoEm)}</span>
                    {noAr && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-green-700 bg-green-100 border border-green-300 rounded px-1.5 py-0.5">
                        <Check size={10} /> no ar
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500 font-mono truncate mt-0.5">
                    {v.geradoPor} · {formatarBytes(v.bytes)} · {v.hashConteudo.slice(0, 12)}
                  </p>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {aoVerCodigo && (
                    <button
                      onClick={() => void verCodigo(v)}
                      disabled={abrindo === v.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50"
                    >
                      {abrindo === v.id ? <Loader2 size={12} className="animate-spin" /> : <Code size={12} />}
                      Ver código
                    </button>
                  )}
                  {/*
                    Na versao que JA esta no ar o botao nao fica inerte: ele
                    reenvia a mesma versao. `sites.reverter` aceita isso — grava
                    o mesmo ponteiro e invalida o KV de novo.

                    Existe porque a invalidacao so acontece NA PUBLICACAO. Quem
                    subiu com as `CF_*` faltando ficou com o KV apontando para a
                    versao anterior, e nao tinha como corrigir depois de
                    configura-las: a entrada velha so sairia sozinha no TTL de
                    24 h, e a unica alternativa era publicar por cima — o que
                    cria uma versao nova so para consertar cache.
                  */}
                  {/*
                    Rascunho nunca vai ao ar, e o botao precisa dizer isso ANTES
                    do clique.

                    `sites.criar` grava uma versao com `chaveR2: ''` e 0 bytes
                    (`sites.service.ts`, geradoPor 'rascunho-inicial'): ela
                    registra a intencao, nao um artefato. `sites.reverter`
                    recusa exatamente essa versao — "Esta versao e um rascunho:
                    nunca teve artefato publicado".

                    Ate aqui a linha aparecia com "Colocar no ar" habilitado, e
                    o clique so devolvia o erro. Um botao que nunca funciona e
                    pior que nenhum botao: ele promete.
                  */}
                  <button
                    onClick={() => void ativar(v)}
                    disabled={trocando !== null || !v.chaveR2}
                    title={
                      !v.chaveR2
                        ? 'Este é o rascunho que nasce junto com o site: ele nunca teve página publicada, então não há o que colocar no ar.'
                        : noAr
                          ? 'Reenvia esta versão à borda e limpa o cache'
                          : undefined
                    }
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                      noAr
                        ? 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
                        : 'text-white bg-black hover:bg-gray-900'
                    }`}
                  >
                    {trocando === v.id ? <Loader2 size={12} className="animate-spin" /> : null}
                    {noAr ? 'Reenviar à borda' : 'Colocar no ar'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
        Trocar a versão move só o ponteiro: o HTML antigo continua no R2 como foi publicado.
        A borda é invalidada na hora. Se o site público ainda mostrar o anterior, use
        <b> Reenviar à borda</b> — e se nem isso resolver, as quatro variáveis
        <code className="font-mono"> CF_*</code> da API não estão configuradas, e a
        entrada do KV só sai sozinha no TTL de 24&nbsp;h.
      </p>
    </div>
  );
};
